import { useEffect, useRef } from "react";
import { Button, Input, Pagination, Row, Space, Spin, Table } from "antd";
import { get } from "lodash";
import Highlighter from "react-highlight-words";
import { SearchOutlined } from "@ant-design/icons";
import { TableLocale } from "@components/noDataFound";
import { ColumnType } from "antd/lib/table";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { usePatientSummaryStore } from "@stores/patientSummaryStore";

interface Provider {
  providerName: string;
  providerNPI: string;
  providerSpecialty: string;
  providerAddress: string;
  providerPhoneNumber: string;
  providerVendorTaxId: string;
}

interface ProviderProps {
  providerData: Provider[];
  providerHistoryData: Provider[];
  setProviderHistoryData: (x: any) => void;
  summaryLoader: boolean;
  sortStrings: (a: Provider, b: Provider, dataIndex: string) => number;
  columnPlaceholder: { [key: string]: string };
  patientID: string;
  prevPatientID: any;
  className: string;
}

const ProviderSummary: React.FC<ProviderProps> = ({ providerData, providerHistoryData, setProviderHistoryData, summaryLoader, sortStrings, columnPlaceholder, patientID, prevPatientID, className }) => {
  const searchInput = useRef<any>(null);
  const columnSearchInfo = usePatientSummaryStore((state) => state.providerSearchText);
  const setColumnSearchInfo = usePatientSummaryStore((state) => state.setProviderSearchText);

  useEffect(() => {
    if (patientID !== prevPatientID) {
      setProviderHistoryData(providerData);
      setColumnSearchInfo({});
    }
  }, [patientID, providerData, prevPatientID, setProviderHistoryData, setColumnSearchInfo]);

  const applyColumnSearch = (dataIndex: string, text: string) => {
    if (text === "") {
      setProviderHistoryData(providerData);
    } else {
      const filteredTableData = providerData?.filter((item: any) => {
        const value = item[dataIndex];
        return value ? value.toString().toLowerCase().includes(text.toLowerCase()) : false;
      });
      setProviderHistoryData(filteredTableData);
    }
  };

  const columnSearch = (dataIndex: any): ColumnType<any> => ({
    filterDropdown: ({ confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${columnPlaceholder[dataIndex] || dataIndex}`}
          value={get(columnSearchInfo, dataIndex, "")}
          onChange={(e) => {
            setColumnSearchInfo({
              ...columnSearchInfo,
              [dataIndex]: e.target.value,
            });
          }}
          onPressEnter={() => {
            applyColumnSearch(dataIndex, columnSearchInfo[dataIndex] || "");
            confirm({ closeDropdown: true });
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              applyColumnSearch(dataIndex, columnSearchInfo[dataIndex] || "");
              confirm({ closeDropdown: true });
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              setColumnSearchInfo({ [dataIndex]: "" });
              applyColumnSearch("", "");
              confirm({ closeDropdown: true });
              clearFilters();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <SearchOutlined
        style={{
          color: get(columnSearchInfo, dataIndex, "") !== "" ? "#EF6D2A" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      const cellValue = record[dataIndex];
      if (cellValue != null) {
        return cellValue
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase());
      }
      return false;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => {
      const formattedText = text || "-";
      return columnSearchInfo[dataIndex] ? (
        <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[columnSearchInfo[dataIndex]]} autoEscape textToHighlight={formattedText} />
      ) : (
        formattedText
      );
    },
  });

  const tableColumns = [
    {
      title: "PROVIDER NAME",
      dataIndex: "providerName",
      width: "16%",
      sorter: (a: any, b: any) => sortStrings(a, b, "providerName"),
      ...columnSearch("providerName"),
    },
    {
      title: "PROVIDER NPI",
      dataIndex: "providerNPI",
      width: "10%",
      sorter: (a: any, b: any) => sortStrings(a, b, "providerNPI"),
      ...columnSearch("providerNPI"),
    },
    {
      title: (
        <>
          PROVIDER <br /> VENDOR TAX ID
        </>
      ),
      dataIndex: "providerVendorTaxId",
      width: "15%",
      sorter: (a: any, b: any) => sortStrings(a, b, "providerVendorTaxId"),
      ...columnSearch("providerVendorTaxId"),
    },
    {
      title: "PROVIDER SPECIALTY",
      dataIndex: "providerSpecialty",
      width: "17%",
      sorter: (a: any, b: any) => sortStrings(a, b, "providerSpecialty"),
      ...columnSearch("providerSpecialty"),
    },
    {
      title: "PROVIDER ADDRESS",
      dataIndex: "providerAddress",
      width: "17%",
      sorter: (a: any, b: any) => sortStrings(a, b, "providerAddress"),
      ...columnSearch("providerAddress"),
    },
    {
      title: (
        <>
          PROVIDER <br /> PHONE NUMBER
        </>
      ),
      dataIndex: "providerPhoneNumber",
      width: "15%",
      sorter: (a: any, b: any) => sortStrings(a, b, "providerPhoneNumber"),
      ...columnSearch("providerPhoneNumber"),
    }
  ];

  return (
    <Spin spinning={summaryLoader} indicator={<HealthemSpinner size={70} />}>
      <Table
        bordered={true}
        columns={tableColumns}
        dataSource={providerHistoryData}
        className={`${className}__table`}
        pagination={{
          defaultPageSize: 5,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "15", "20", "25", "30"],
          showTotal: () => <span className={`${className}__table__footer`}>Note: Data displayed is from last 24 months</span>,
        }}
        locale={TableLocale}
        scroll={{ x: 'max-content' }}
      />
    </Spin>
  );
};

export default ProviderSummary;
